<template>
  <div class="interval-picker">
    <DateIntervalPicker
      v-if="withDate"
      class="interval-picker__item"
      :displayFormat="displayDateFormat"
      :date="date"
      :errors="errors?.date"
      :outputFormat="outputDateFormat"
      :mode="dateMode"
      :rangePickerWithTime="rangePickerWithTime"
      :layout="layout"
      :disabledStart="disabledDateStart"
      :disabledEnd="disabledDateEnd"
      :disabledDateRuleFn="disabledDateRuleFn"
      :allowEqualStartAndEndDays="allowEqualStartAndEndDays"
      :customPopupContainer="customPopupContainer"
      :allowClear="allowClear"
      @change="handleDateTimeChange('date', $event)"
    />
    <TimeIntervalPicker
      v-if="withTime"
      class="interval-picker__item"
      :time="time"
      :errors="errors?.time"
      :outputFormat="outputTimeFormat"
      :mode="timeMode"
      :layout="layout"
      :disabledStart="disabledTime"
      :disabledEnd="disabledTime"
      :minuteStep="minuteStep"
      @change="handleDateTimeChange('time', $event)"
    />
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  PropType,
  toRaw,
} from 'vue';

import { DATE_FORMAT, ELayout } from '@/constants';
import { EDateTimeType } from '@/domains/constants';
import { TPickerOptions, TDateAppFormat } from '@/domains/types';
import { parseDateToAppFormat } from '@/utils/dateUtils';

import {
  TDateTimeDeterminedType,
  TDateTimeIntervalType,
  TDateTimeInterval,
} from './domain/types';
import DateIntervalPicker from '../DateIntervalPicker/index.vue';
import TimeIntervalPicker from '../TimeIntervalPicker/index.vue';
import { EIntervalMode } from './domain/constants';

const inputPropByField = {
  time: 'inputTimeFormat',
  date: 'inputDateFormat',
};

export default defineComponent({
  name: 'IntervalPicker',
  components: {
    DateIntervalPicker,
    TimeIntervalPicker,
  },
  props: {
    data: {
      type: [Object, null] as PropType<Partial<TDateTimeInterval> | null>,
      default: null,
    },
    withDate: {
      type: Boolean,
      default: true,
    },
    withTime: {
      type: Boolean,
      default: true,
    },
    rangePickerWithTime: {
      type: Boolean,
      default: false,
    },
    inputDateFormat: {
      type: String,
      default: DATE_FORMAT.fullDate,
    },
    inputTimeFormat: {
      type: String,
      default: DATE_FORMAT.hoursMinutes,
    },
    outputDateFormat: {
      type: String,
      default: DATE_FORMAT.fullDate,
    },
    outputTimeFormat: {
      type: String,
      default: DATE_FORMAT.hoursMinutesTimezone,
    },
    displayDateFormat: {
      type: String,
      default: DATE_FORMAT.fullDate,
    },
    displayTimeFormat: {
      type: String,
      default: DATE_FORMAT.hoursMinutes,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    dateMode: {
      type: String as PropType<EIntervalMode>,
      default: EIntervalMode.singleAndInterval,
    },
    timeMode: {
      type: String as PropType<EIntervalMode>,
      default: EIntervalMode.singleAndInterval,
    },
    layout: {
      type: String as PropType<ELayout>,
      default: ELayout.vertical,
    },
    disabledDateStart: {
      type: Boolean,
      default: false,
    },
    disabledDateEnd: {
      type: Boolean,
      default: false,
    },
    disabledTime: {
      type: Boolean,
      default: false,
    },
    disabledDateRuleFn: {
      type: Function as PropType<(_date: TDateAppFormat) => boolean>,
      default: () => false,
    },
    allowEqualStartAndEndDays: {
      type: Boolean,
      default: true,
    },
    customPopupContainer: {
      type: Boolean,
      default: true,
    },
    allowClear: {
      type: Boolean,
      default: true,
    },
    minuteStep: {
      type: Number,
      default: 1,
    },
  },
  emits: ['change'],
  setup(props, { emit }) {
    const getIsDateTimeDeterminedType = (dateTime: TDateTimeInterval['date'] | TDateTimeInterval['time']): dateTime is TDateTimeDeterminedType => dateTime.type === EDateTimeType.determined;
    const getIsDateTimeIntervalType = (dateTime: TDateTimeInterval['date'] | TDateTimeInterval['time']): dateTime is TDateTimeIntervalType => dateTime.type === EDateTimeType.interval;

    const handleDateTimeChange = (field: 'date' | 'time', payload: any) => {
      // @ts-ignore
      const { [field]: targetField, ...restFields } = toRaw(props.data) || {};

      const { type, start, end } = payload;

      const result: TDateTimeInterval = { ...restFields as TDateTimeInterval };

      if (type === EDateTimeType.interval) {
        const newValue: TDateTimeIntervalType = {
          type,
          startsAt: start,
          endsAt: end,
        };
        result[field] = newValue;
      } else if (type === EDateTimeType.determined) {
        const newValue: TDateTimeDeterminedType = {
          type,
          exactValue: start,
        };
        result[field] = newValue;
      }

      emit('change', result);
    };

    const getFieldFromProps = (field: 'date' | 'time') => {
      const result = {} as TPickerOptions;
      if (!props.data?.[field]) return result;
      const dateTimeFormat = props[inputPropByField[field]];
      const dateTimeByField = props.data[field];
      const isDeterminedType = getIsDateTimeDeterminedType(dateTimeByField);
      const isIntervalType = getIsDateTimeIntervalType(dateTimeByField);

      if (isDeterminedType) {
        result.start = parseDateToAppFormat(dateTimeByField.exactValue, dateTimeFormat);
      } else if (isIntervalType) {
        result.interval = true;
        result.start = parseDateToAppFormat(dateTimeByField.startsAt, dateTimeFormat);
        result.end = parseDateToAppFormat(dateTimeByField.endsAt, dateTimeFormat);
      }

      return result;
    };
    const date = computed(() => getFieldFromProps('date'));
    const time = computed(() => getFieldFromProps('time'));

    return {
      date,
      time,

      handleDateTimeChange,
    };
  },
});
</script>

<style lang="scss" src="./styles.scss" />
